package com.evoapps.evoshift.domain.model

import kotlinx.datetime.LocalDate
import kotlin.time.Instant

/*
 * SH-122: documents per person and workplace policies.
 *
 * Where they live (docs/compliance/gdpr-engineering.md, "Documents"):
 * - a person's documents at one workplace: the file in Cloud Storage at
 *   users/{uid}/staff_documents/{wpId}/{docId}, its description in Firestore at
 *   users/{uid}/staff_documents/{docId} ([StaffDocument]). Read by the person and that workplace's
 *   owner and financial managers (the HR readers of the SH-47 dates, firestore.rules and
 *   storage.rules), never by colleagues or operational managers: contracts carry pay, a medical
 *   certificate is health-related (GDPR Art. 9).
 * - a workplace's policies (rules, OHS instruction): a PDF at workplaces/{wpId}/policies/{id},
 *   described at workplaces/{wpId}/policies/{id} ([WorkplacePolicy]); every member reads them.
 * - "I have read it": workplaces/{wpId}/policies/{id}/acknowledgements/{uid}
 *   ([PolicyAcknowledgement]), written only by the acknowledgePolicy function with the server's
 *   time. A confirmation that a document was read: NOT a qualified electronic signature (eIDAS
 *   art. 3 pkt 12) and no signature of the document at all.
 */

/** What a person's document is. */
enum class StaffDocumentType {
    /** The employment or civil-law contract, an annex. */
    CONTRACT,
    /** The occupational medical certificate (orzeczenie lekarskie, art. 229 KP): fit or not, no results. */
    MEDICAL_EXAM,
    /** An OHS training certificate (zaświadczenie BHP, art. 237³ KP). */
    OHS,
    OTHER;

    /** The SH-47 check whose "valid until" this document's expiry date records (null: none). */
    val check: StaffCheck?
        get() = when (this) {
            MEDICAL_EXAM -> StaffCheck.MEDICAL_EXAM
            OHS -> StaffCheck.OHS_TRAINING
            CONTRACT, OTHER -> null
        }
}

/** One file about a person at one workplace. */
data class StaffDocument(
    val id: String,
    val userId: String,
    val workplaceId: String,
    val type: StaffDocumentType,
    /** The file's name as uploaded (shown and used when it is opened), see [DocumentFiles.cleanFileName]. */
    val fileName: String,
    /** One of [DocumentFiles.STAFF_TYPES], worked out from the file's content. */
    val contentType: String,
    val sizeBytes: Long,
    /** The last day the document is valid; null for documents that do not expire. */
    val expiresOn: LocalDate? = null,
    val uploadedAt: Instant? = null,
    val uploadedBy: String? = null
) {
    val storagePath: String get() = DocumentFiles.staffPath(userId, workplaceId, id)
}

/** A document of a workplace everyone working there is asked to read (a PDF). */
data class WorkplacePolicy(
    val id: String,
    val workplaceId: String,
    val title: String,
    val fileName: String,
    val sizeBytes: Long,
    val publishedAt: Instant? = null,
    val publishedBy: String? = null
) {
    val storagePath: String get() = DocumentFiles.policyPath(workplaceId, id)
}

/** [userId] confirmed reading the policy at [acknowledgedAt] (the server's clock). */
data class PolicyAcknowledgement(
    val policyId: String,
    val workplaceId: String,
    val userId: String,
    val acknowledgedAt: Instant,
    /** The name the person had when they confirmed (people who left stay recognisable). */
    val displayName: String? = null
)

/** Why a file cannot be uploaded. */
enum class DocumentProblem {
    EMPTY,
    /** Over [DocumentFiles.MAX_BYTES]. */
    TOO_LARGE,
    /** Not a PDF, JPEG or PNG (policies: not a PDF). */
    UNSUPPORTED_TYPE,
    /** A policy without a title. */
    MISSING_TITLE
}

class InvalidDocumentException(val problem: DocumentProblem) : IllegalArgumentException("Invalid document: $problem")

/** The files' rules, shared by the app, firestore.rules and storage.rules (keep them in step). */
object DocumentFiles {
    const val MAX_BYTES: Int = 10 * 1024 * 1024
    const val PDF = "application/pdf"
    const val JPEG = "image/jpeg"
    const val PNG = "image/png"

    /** What a person's document may be: a PDF or a photo / scan. */
    val STAFF_TYPES: Set<String> = setOf(PDF, JPEG, PNG)

    const val MAX_FILE_NAME = 120
    const val MAX_POLICY_TITLE = 120

    fun staffPath(userId: String, workplaceId: String, documentId: String): String =
        "users/${segment(userId)}/staff_documents/${segment(workplaceId)}/${segment(documentId)}"

    fun policyPath(workplaceId: String, policyId: String): String =
        "workplaces/${segment(workplaceId)}/policies/${segment(policyId)}"

    private fun segment(value: String): String {
        require(value.isNotBlank() && '/' !in value) { "Invalid path segment" }
        return value
    }

    /**
     * The content type of [bytes] from their first bytes (not the file name, which can lie):
     * PDF, JPEG or PNG, and (SH-211) a PowerPoint .pptx or an Impress .odp presentation; null for
     * anything else. A presentation is a ZIP archive: .odp names its type in the uncompressed
     * "mimetype" entry that opens the archive, .pptx has a "ppt/presentation.xml" entry.
     */
    fun sniff(bytes: ByteArray): String? {
        fun startsWith(vararg prefix: Int) = bytes.size >= prefix.size && prefix.indices.all { bytes[it] == prefix[it].toByte() }
        return when {
            startsWith(0x25, 0x50, 0x44, 0x46, 0x2D) -> PDF // %PDF-
            startsWith(0xFF, 0xD8, 0xFF) -> JPEG
            startsWith(0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A) -> PNG
            startsWith(0x50, 0x4B, 0x03, 0x04) -> zipPresentation(bytes) // PK\u0003\u0004
            else -> null
        }
    }

    private fun ByteArray.hasAsciiAt(offset: Int, text: String): Boolean =
        offset >= 0 && offset + text.length <= size && text.indices.all { this[offset + it] == text[it].code.toByte() }

    private fun ByteArray.containsAscii(text: String): Boolean {
        val first = text[0].code.toByte()
        for (i in 0..size - text.length) if (this[i] == first && hasAsciiAt(i, text)) return true
        return false
    }

    private fun zipPresentation(bytes: ByteArray): String? {
        // The first local file header: name length at 26, extra length at 28, the name at 30.
        if (bytes.size < 30) return null
        val nameLength = (bytes[26].toInt() and 0xFF) or ((bytes[27].toInt() and 0xFF) shl 8)
        val extraLength = (bytes[28].toInt() and 0xFF) or ((bytes[29].toInt() and 0xFF) shl 8)
        val odpType = "application/vnd.oasis.opendocument.presentation"
        return when {
            nameLength == 8 && bytes.hasAsciiAt(30, "mimetype") && bytes.hasAsciiAt(30 + 8 + extraLength, odpType) &&
                !bytes.hasAsciiAt(30 + 8 + extraLength + odpType.length, "-") -> PresentationFiles.ODP
            bytes.containsAscii("[Content_Types].xml") && bytes.containsAscii("ppt/presentation.xml") -> PresentationFiles.PPTX
            else -> null
        }
    }

    /** What a downloaded document may be opened as ([openable]): documents, photos, presentations. */
    val OPENABLE_TYPES: Set<String> = STAFF_TYPES + setOf(PresentationFiles.PPTX, PresentationFiles.ODP)

    /**
     * What is wrong with [bytes] as a document of [allowed] types, or null when it may be
     * uploaded (then [sniff] gives its content type).
     */
    fun problemOf(bytes: ByteArray, allowed: Set<String> = STAFF_TYPES): DocumentProblem? = when {
        bytes.isEmpty() -> DocumentProblem.EMPTY
        bytes.size > MAX_BYTES -> DocumentProblem.TOO_LARGE
        sniff(bytes)?.let { it in allowed } != true -> DocumentProblem.UNSUPPORTED_TYPE
        else -> null
    }

    /**
     * [name] without folders and control characters, at most [MAX_FILE_NAME] characters with its
     * extension kept, ending in [contentType]'s extension; "document.<ext>" when nothing is left.
     */
    fun cleanFileName(name: String?, contentType: String): String {
        val extension = when (contentType) {
            PDF -> "pdf"
            JPEG -> "jpg"
            PNG -> "png"
            PresentationFiles.PPTX -> "pptx"
            PresentationFiles.ODP -> "odp"
            else -> "bin"
        }
        val base = name.orEmpty().substringAfterLast('/').substringAfterLast('\\')
            .filter { !it.isISOControl() && it !in "<>:\"|?*" }
            .trim().trimStart('.')
        val stem = base.substringBeforeLast('.', base).trim().ifBlank { "document" }
        val known = base.substringAfterLast('.', "").lowercase()
        val ext = if (known == extension || (extension == "jpg" && known == "jpeg")) known else extension
        return stem.take(MAX_FILE_NAME - ext.length - 1) + "." + ext
    }

    /**
     * A downloaded document as it may be handed to the system's viewer (SH-177): its type is read
     * from [bytes], never from the stored name or type (a Firestore field another client can set),
     * and the file name is [cleanFileName] with that type's extension. So "payslip.command" with a
     * PDF inside opens as payslip.pdf, and bytes that are not a PDF, JPEG, PNG or (SH-211) a .pptx / .odp
     * presentation are not opened at all (null): a desktop would otherwise run a script named after the document.
     */
    fun openable(name: String?, bytes: ByteArray): OpenableDocument? {
        val type = sniff(bytes)?.takeIf { it in OPENABLE_TYPES } ?: return null
        return OpenableDocument(cleanFileName(name, type), type)
    }
}

/** See [DocumentFiles.openable]. */
data class OpenableDocument(val fileName: String, val contentType: String)

/** A policy with whether (and when) the reader confirmed it. */
data class PolicyStatus(
    val policy: WorkplacePolicy,
    /** The reader's own confirmation; null: not yet. */
    val acknowledgement: PolicyAcknowledgement? = null
) {
    val isAcknowledged: Boolean get() = acknowledgement != null
}

/** Who of a team confirmed a policy (managers). */
data class PolicyReadReport(
    val policy: WorkplacePolicy,
    /** Confirmations, oldest first; also of people who no longer work there. */
    val acknowledged: List<PolicyAcknowledgement>,
    /** Current team members who have not confirmed yet: uid → display name. */
    val pending: List<Pair<String, String>>,
    /** How many people work there now. */
    val teamSize: Int = pending.size
) {
    /** How many of the current team confirmed. */
    val confirmedOfTeam: Int get() = teamSize - pending.size
}
